import { createPortal } from "react-dom";

import { Dropdown } from "react-bootstrap";

import {
  Card,
  CardGrid,
  FixedToolbarItem,
  FlexToolbarItem,
  Toolbar,
} from "@bagel-web/components";

import ComponentConfigCard from "./ComponentConfigCard";
import { SUPPORTED_COMPONENTS } from "./supportedComponents";
import { ServiceParamValue } from "../../service/ServiceParamValueField";
import { PlaygroundComponentConfig } from "../../types";

// The Components card: the Add menu over the supported types, and a card
// per configured component.
const ComponentsCard = ({
  componentConfigs,
  onAdd,
  onFieldChange,
  onDelete,
  onRename,
}: {
  componentConfigs: Array<PlaygroundComponentConfig>;
  // A new component of the type, presenting the given element.
  onAdd: (type: string, element: string) => void;
  onFieldChange: (
    component: PlaygroundComponentConfig,
    name: string,
    value: ServiceParamValue
  ) => void;
  onDelete?: (component: PlaygroundComponentConfig) => void;
  onRename?: (component: PlaygroundComponentConfig, nextId: string) => void;
}) => (
  <Card>
    <Toolbar className="mb-3">
      <FlexToolbarItem>
        {/* The title lives in the toolbar so Add sits inline with it;
            card-title h5 matches the Card title prop's rendering. */}
        <div className="card-title h5 mb-0">Components</div>
      </FlexToolbarItem>
      <FixedToolbarItem>
        <Dropdown>
          <Dropdown.Toggle
            variant="secondary"
            data-testid="playground-component-add"
          >
            <i className="bi-plus-lg me-1" />
            Add
          </Dropdown.Toggle>
          {/* Portaled to the body so revealing the menu can't grow the
              card; the dropdown context reaches it through the portal. */}
          {createPortal(
            <Dropdown.Menu>
              {SUPPORTED_COMPONENTS.map(({ type, elements }) => (
                <Dropdown.Item
                  key={type}
                  className="font-monospace"
                  onClick={() => onAdd(type, elements[0]!)}
                  data-testid={`playground-component-add-${type}`}
                >
                  {type}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>,
            document.body
          )}
        </Dropdown>
      </FixedToolbarItem>
    </Toolbar>
    {componentConfigs.length ? (
      // Channels are deliberately not shown: they're invisible
      // throughout the Playground UX.
      <div data-testid="playground-component-configs">
        <CardGrid maxColumns={1}>
          {componentConfigs.map((component, index) => (
            // Keyed by position: the id is editable, and an id-based key
            // would remount the card (dropping focus) on every rename
            // keystroke.
            <ComponentConfigCard
              key={index}
              index={index}
              component={component}
              takenIds={
                new Set(
                  componentConfigs
                    .filter((candidate) => candidate !== component)
                    .map((candidate) =>
                      typeof candidate.id === "string" ? candidate.id : ""
                    )
                )
              }
              onFieldChange={(name, value) =>
                onFieldChange(component, name, value)
              }
              onDelete={() => onDelete?.(component)}
              onRename={onRename}
            />
          ))}
        </CardGrid>
      </div>
    ) : (
      <p className="text-body-secondary mb-0">No components.</p>
    )}
  </Card>
);

export default ComponentsCard;
